import { Archive, CloudUpload, ImageOff, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ATTACHMENT_ACCEPT,
  isImage,
  type Attachment,
  type SupportedMimeType,
} from '@obraplan/core/domain/attachments/attachment';
import type { AttachmentUsage } from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/dialog';
import { estaNaFila } from './fotos-na-fila';
import { PhotoSourceButtons } from './PhotoSourceButtons';

/**
 * A galeria de fotos — da conferência (Q16) e do relatório fotográfico do RDO (Fase 18).

 * Uma só para os dois donos, porque as duas fazem a mesma coisa: mostram miniatura,
 * contam quantas cabem ainda e dizem quanto de cota já foi. O que muda é a legenda, e
 * quem decide se ela existe é quem monta a galeria — a regra não é sobre o arquivo, é
 * sobre onde ele aparece (RN-214).
 *
 *
 * Pensada para o tablet no canteiro: quem está de pé na frente de serviço fotografa na
 * hora, e por isso **tirar foto** é o botão grande e primeiro. **Anexar** fica ao lado,
 * para o laudo em PDF e para a foto que já está no aparelho — os dois caminhos existem
 * porque os dois acontecem (`PhotoSourceButtons`). A miniatura aparece assim que o upload
 * termina, e é ela que confirma que a foto chegou.
 *
 * Cada miniatura tem uma `objectURL` viva. Elas são **revogadas na desmontagem e a cada
 * troca de lista**: sem isso, uma conferência com dez fotos abertas e fechadas vinte vezes
 * mantém duzentos blobs na memória do tablet até a aba ser fechada (ADR-109).
 */
export interface AttachmentGalleryProps {
  attachments: readonly Attachment[];
  usage: AttachmentUsage | undefined;
  canAttach: boolean;
  canRemove: boolean;
  isPending: boolean;
  errorMessage?: string;
  /**
   * A legenda da próxima foto — presente só onde ela é exigida (RN-214).
   *
   * No relatório fotográfico ela é o que diz o que se está vendo — sem ela a segunda
   * página é um mosaico que só quem esteve no canteiro reconhece, e o documento existe
   * para quem não esteve. Na conferência ela existe do mesmo jeito, mas com
   * `required: false`: a foto ali já tem dono e data, e exigir texto para anexá-la
   * custaria a evidência do dia por causa de uma frase.
   *
   * O valor mora em quem monta a galeria, e não aqui: é quem chama `onAttach` que o
   * envia junto com o arquivo, e guardá-lo em dois lugares os faria divergir.
   */
  caption?: { value: string; onChange: (value: string) => void; required?: boolean } | undefined;
  /** Resolve a URL de exibição de um anexo — o binário só é lido aqui. */
  resolveUrl: (attachmentId: string) => Promise<string | undefined>;
  onAttach: (files: readonly File[]) => void;
  onRemove: (attachmentId: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentGallery({
  attachments,
  usage,
  canAttach,
  canRemove,
  isPending,
  errorMessage,
  caption,
  resolveUrl,
  onAttach,
  onRemove,
}: AttachmentGalleryProps) {
  const [urls, setUrls] = useState<Readonly<Record<string, string>>>({});
  const [zoomed, setZoomed] = useState<Attachment | undefined>(undefined);
  /**
   * A foto que o usuário pediu para remover, à espera da confirmação.
   *
   * Uma foto de conferência não se refaz: a frente de serviço de ontem já não está como
   * estava, e o toque errado no botãozinho de 20px — com o tablet na mão e luva de obra —
   * apaga a única evidência do dia (Q16). Editar horas no quadro se desfaz digitando o
   * número de novo; isto não.
   */
  const [pendingRemoval, setPendingRemoval] = useState<Attachment | undefined>(undefined);

  const key = attachments.map((item) => item.id).join('|');

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];

    void (async () => {
      const entries: [string, string][] = [];
      for (const attachment of attachments) {
        const url = await resolveUrl(attachment.id);
        if (url === undefined) continue;
        created.push(url);
        entries.push([attachment.id, url]);
      }
      if (cancelled) {
        // A lista mudou enquanto as URLs eram resolvidas: nada aqui será exibido, e
        // deixá-las vivas seria vazar exatamente o que este efeito existe para conter.
        created.forEach((url) => {
          URL.revokeObjectURL(url);
        });
        return;
      }
      setUrls(Object.fromEntries(entries));
    })();

    return () => {
      cancelled = true;
      created.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
    // `key` representa a lista: um array novo a cada render reabriria os blobs sem parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, resolveUrl]);

  const full = usage !== undefined && usage.count >= usage.limit;
  // Sem legenda o caso de uso recusa (RN-214). Bloquear aqui evita a foto ser comprimida
  // e enviada para voltar como erro — com o tablet na mão, no canteiro, e a frente de
  // serviço já desmontada.
  const legendaObrigatoria = caption !== undefined && caption.required !== false;
  const semLegenda = legendaObrigatoria && caption.value.trim() === '';
  const disabled = full || isPending || semLegenda;

  return (
    <section className="flex flex-col gap-3">
      {/*
        Sem título próprio: quem nomeia a seção é o diálogo que a contém, e repetir
        "Fotos da conferência" duas vezes na mesma caixa não informa nada.
      */}
      <header className="flex flex-wrap items-center justify-between gap-2">
        {usage !== undefined && (
          <p className="text-xs text-(--color-content-muted)">
            {usage.count} de {usage.limit} fotos · {formatBytes(usage.storedBytes)}
          </p>
        )}
      </header>

      {attachments.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-(--color-content-muted)">
          <ImageOff className="size-4" aria-hidden="true" />
          {legendaObrigatoria
            ? 'Nenhuma foto no relatório fotográfico deste dia.'
            : 'Nenhuma foto anexada a esta conferência.'}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <li key={attachment.id} className="relative flex w-24 flex-col gap-1">
              {attachment.archivedAt === undefined ? (
                <button
                  type="button"
                  className="block size-24 overflow-hidden rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted)"
                  onClick={() => {
                    setZoomed(attachment);
                  }}
                  aria-label={`Ampliar ${attachment.originalName}`}
                >
                  <Thumbnail
                    url={urls[attachment.id]}
                    name={attachment.originalName}
                    mimeType={attachment.mimeType}
                  />
                </button>
              ) : (
                /*
                  Arquivada: o registro ficou, o arquivo saiu para um `.zip` guardado fora
                  daqui. Mostrar a moldura vazia é o ponto — sem ela, a conferência de um
                  ano atrás pareceria uma conferência feita sem foto nenhuma.
                */
                <div
                  className="flex size-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-(--color-border-subtle) bg-(--color-surface-muted) p-1 text-center"
                  title={`${attachment.originalName} — arquivada`}
                >
                  <Archive className="size-4 text-(--color-content-muted)" aria-hidden="true" />
                  <span className="text-[10px] leading-tight text-(--color-content-muted)">
                    Arquivada em {attachment.archivedAt.slice(0, 10).split('-').reverse().join('/')}
                  </span>
                </div>
              )}
              {canRemove && attachment.archivedAt === undefined && (
                <button
                  type="button"
                  className="absolute -right-1 -top-1 rounded-full border border-(--color-border-subtle) bg-(--color-surface) p-1 text-(--color-state-danger)"
                  aria-label={`Remover ${attachment.originalName}`}
                  onClick={() => {
                    setPendingRemoval(attachment);
                  }}
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </button>
              )}
              {/* A foto tirada sem sinal ainda está no aparelho (RN-622): sobe sozinha. */}
              {estaNaFila(attachment.id) && (
                <p className="flex items-center gap-1 text-[10px] leading-tight text-(--color-state-info)">
                  <CloudUpload className="size-3 shrink-0" aria-hidden="true" />
                  Aguardando envio
                </p>
              )}
              {/* A legenda é o que o relatório fotográfico imprime embaixo da foto; aqui
                  ela aparece pelo mesmo motivo — para conferir antes de o papel sair. */}
              {attachment.caption !== undefined && (
                <p className="text-[10px] leading-tight text-(--color-content-muted)">
                  {attachment.caption}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {canAttach && (
        <div className="flex flex-col gap-1">
          {caption !== undefined && (
            <label className="mb-1 flex flex-col gap-1 text-xs text-(--color-content-muted)">
              {legendaObrigatoria ? 'Legenda da próxima foto' : 'Descrição da próxima foto'}
              <Input
                value={caption.value}
                placeholder="FABRICAÇÃO DE FORMA DE ESCADA"
                onChange={(event) => {
                  caption.onChange(event.target.value);
                }}
              />
            </label>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <PhotoSourceButtons
              accept={ATTACHMENT_ACCEPT}
              disabled={disabled}
              isPending={isPending}
              onFiles={onAttach}
            />
          </div>

          {semLegenda && (
            <p className="text-xs text-(--color-content-muted)">
              Escreva a legenda antes de anexar: é ela que diz o que se está vendo no relatório
              fotográfico.
            </p>
          )}
          {full && (
            <p className="text-xs text-(--color-content-muted)">
              Limite de {usage?.limit} fotos atingido.
            </p>
          )}
        </div>
      )}

      {errorMessage !== undefined && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {errorMessage}
        </p>
      )}

      <ConfirmDialog
        open={pendingRemoval !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(undefined);
        }}
        destructive
        title="Remover esta foto?"
        description={
          pendingRemoval !== undefined && estaNaFila(pendingRemoval.id)
            ? `A foto ${pendingRemoval.originalName} ainda não subiu: sai do aparelho e não vai mais para o servidor.`
            : 'A foto ' +
              (pendingRemoval?.originalName ?? '') +
              (legendaObrigatoria
                ? ' sai do relatório fotográfico deste dia.'
                : ' sai da conferência e deixa de contar como evidência do dia.') +
              ' A frente de serviço já não está como estava — não há como tirar esta foto de novo.'
        }
        confirmLabel="Remover foto"
        onConfirm={() => {
          const target = pendingRemoval;
          setPendingRemoval(undefined);
          if (target !== undefined) onRemove(target.id);
        }}
      />

      {zoomed !== undefined && (
        <ZoomedAttachment
          attachment={zoomed}
          url={urls[zoomed.id]}
          onClose={() => {
            setZoomed(undefined);
          }}
        />
      )}
    </section>
  );
}

function Thumbnail({
  url,
  name,
  mimeType,
}: {
  url: string | undefined;
  name: string;
  mimeType: SupportedMimeType;
}) {
  if (!isImage(mimeType)) {
    return (
      <span className="flex size-full flex-col items-center justify-center gap-1 p-1 text-center text-xs">
        PDF
        <span className="line-clamp-2 text-(--color-content-muted)">{name}</span>
      </span>
    );
  }
  if (url === undefined) {
    return <span className="flex size-full items-center justify-center text-xs">…</span>;
  }
  return <img src={url} alt={name} className="size-full object-cover" />;
}

/**
 * O visualizador ampliado.
 *
 * Sem biblioteca de lightbox: uma sobreposição, a imagem inteira e `Esc` para fechar.
 *
 * Vai para `document.body` por portal, e não é decoração: o diálogo que contém a galeria
 * aplica `transform` na própria caixa, e um ancestral transformado faz `position: fixed`
 * passar a se resolver contra ele — a sobreposição ficaria recortada dentro do diálogo,
 * que é exatamente o que ela existe para escapar.
 */
function ZoomedAttachment({
  attachment,
  url,
  onClose,
}: {
  attachment: Attachment;
  url: string | undefined;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={attachment.originalName}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/80 p-4"
      onClick={onClose}
    >
      {url === undefined ? (
        <p className="text-sm text-white">Arquivo indisponível.</p>
      ) : isImage(attachment.mimeType) ? (
        <img
          src={url}
          alt={attachment.originalName}
          className="max-h-[80vh] max-w-full object-contain"
        />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-white underline">
          Abrir {attachment.originalName}
        </a>
      )}

      <p className="text-xs text-white/80">
        {attachment.originalName} · {formatBytes(attachment.compressedSizeBytes)}
      </p>

      <Button variant="secondary" onClick={onClose}>
        <X aria-hidden="true" />
        Fechar
      </Button>
    </div>,
    document.body,
  );
}
